/**
 * ServiceDesignerRelationTypes — manage the configurable relation-type catalog
 * (spec §2B, §17.1). Lists existing types, creates new ones and edits them.
 * Relation types are NOT hardcoded: an admin can define new ones anytime.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import './ServiceDesigner.css';

const CARDINALITIES = ['N:N', '1:N', 'N:1', '1:1'];

const TYPE_OPTIONS = [
  { value: '', label: '— Cualquier tipo —' },
  { value: 'service', label: 'Servicio' },
  { value: 'rack_element', label: 'Servidor' },
  { value: 'virt_vm', label: 'VM' },
  { value: 'printer', label: 'Impresora' },
  { value: 'wifi_controller', label: 'Controladora WiFi' },
  { value: 'wifi_access_point', label: 'AP WiFi' },
  { value: 'firewall', label: 'Firewall' },
  { value: 'router', label: 'Router' },
  { value: 'workstation', label: 'Puesto' },
  { value: 'device', label: 'Dispositivo' },
  { value: 'storage_disk', label: 'Disco' },
  { value: 'isp_circuit', label: 'Circuito ISP' },
  { value: 'ipam_ip', label: 'IP' },
];

const emptyForm = () => ({
  name: '', display_name: '', source_type: '', target_type: '', cardinality: 'N:N', inverse_name: '', description: '', is_active: true,
});

export default function ServiceDesignerRelationTypes({ companyId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.sdListRelationTypes(companyId);
      setRows(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const edit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '', display_name: row.display_name || '',
      source_type: row.source_type || '', target_type: row.target_type || '',
      cardinality: row.cardinality || 'N:N', inverse_name: row.inverse_name || '',
      description: row.description || '', is_active: row.is_active !== false,
    });
  };

  const reset = () => { setEditingId(null); setForm(emptyForm()); };

  const save = async () => {
    if (!form.name.trim() || !form.display_name.trim()) {
      setError('El nombre (clave) y el nombre visible son obligatorios.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        display_name: form.display_name.trim(),
        source_type: form.source_type || null,
        target_type: form.target_type || null,
        cardinality: form.cardinality || 'N:N',
        inverse_name: form.inverse_name.trim() || null,
        description: form.description.trim() || null,
        is_active: form.is_active,
      };
      if (editingId) {
        await api.sdUpdateRelationType(editingId, companyId, payload);
      } else {
        await api.sdCreateRelationType(companyId, payload);
      }
      reset();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-modal-backdrop" onClick={onClose}>
      <div className="sd-modal sd-model-modal sd-rt" onClick={(e) => e.stopPropagation()}>
        <div className="sd-model-head">
          <h3>🔗 Tipos de relación</h3>
          <button className="sd-model-x" onClick={onClose} title="Cerrar"><XMarkIcon width={16} height={16} /></button>
        </div>
        <p className="sd-modal-sub">
          Catálogo configurable: añade o edita relaciones sin tocar código (spec §17.1).
        </p>

        {error && <div className="sd-error-banner">{error}<button onClick={() => setError(null)}><XMarkIcon width={14} height={14} /></button></div>}

        {/* Lista */}
        <div className="sd-builder-section">
          <p className="sd-builder-section-title">Relaciones ({rows.length})</p>
          {loading ? (
            <p className="sd-hint">Cargando…</p>
          ) : (
            <div className="sd-builder-list">
              {rows.map((rt) => (
                <div key={rt.id} className="sd-builder-row">
                  <span className="sd-builder-row-name">{rt.display_name} <em className="sd-rt-code">({rt.name})</em></span>
                  <span className="sd-builder-row-meta">
                    {rt.cardinality}{rt.source_type ? ` · ${rt.source_type}→` : ' · cualquiera→'}{rt.target_type || 'cualquiera'}
                  </span>
                  <button className="sd-rt-edit" onClick={() => edit(rt)} title="Editar">✎</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Formulario crear/editar */}
        <div className="sd-builder-section">
          <p className="sd-builder-section-title">{editingId ? '✎ Editar relación' : '＋ Nueva relación'}</p>
          <div className="sd-rt-form">
            <div className="sd-rt-grid2">
              <div>
                <label className="sd-builder-label">Clave (name)</label>
                <input className="sd-builder-input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="p. ej. consume_api_de" />
              </div>
              <div>
                <label className="sd-builder-label">Nombre visible</label>
                <input className="sd-builder-input" value={form.display_name} onChange={(e) => set('display_name', e.target.value)} placeholder="p. ej. consume API de" />
              </div>
            </div>
            <div className="sd-rt-grid2">
              <div>
                <label className="sd-builder-label">Tipo origen (opcional)</label>
                <select className="sd-builder-input" value={form.source_type} onChange={(e) => set('source_type', e.target.value)}>
                  {TYPE_OPTIONS.map((o) => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="sd-builder-label">Tipo destino (opcional)</label>
                <select className="sd-builder-input" value={form.target_type} onChange={(e) => set('target_type', e.target.value)}>
                  {TYPE_OPTIONS.map((o) => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="sd-rt-grid2">
              <div>
                <label className="sd-builder-label">Cardinalidad</label>
                <select className="sd-builder-input" value={form.cardinality} onChange={(e) => set('cardinality', e.target.value)}>
                  {CARDINALITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="sd-builder-label">Inversa (opcional)</label>
                <input className="sd-builder-input" value={form.inverse_name} onChange={(e) => set('inverse_name', e.target.value)} placeholder="p. ej. provee API a" />
              </div>
            </div>
            <div>
              <label className="sd-builder-label">Descripción</label>
              <textarea className="sd-builder-input sd-builder-area" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Opcional" />
            </div>
            {editingId && (
              <label className="sd-builder-req sd-rt-active" style={{ marginTop: 8 }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} /> Activa
              </label>
            )}
          </div>
          <button className="sd-builder-add" style={{ marginTop: 8 }} onClick={reset} disabled={!editingId && !form.name && !form.display_name}>
            <PlusIcon width={14} height={14} /> Nueva
          </button>
        </div>

        <div className="sd-modal-actions">
          <button className="sd-cancel-btn" onClick={onClose}>Cerrar</button>
          <button className="sd-save-btn" disabled={saving} onClick={save}>
            {saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear relación'}
          </button>
        </div>
      </div>
    </div>
  );
}
